// Cognito Hosted UI sign-in with PKCE through Amplify (requirements 1.1-1.5; design 3.2, 3.3, 4.6).
import { Amplify } from 'aws-amplify';
import { fetchAuthSession, signInWithRedirect, signOut as amplifySignOut } from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { sessionStorage as amplifySessionStorage } from 'aws-amplify/utils';
import { ROLES, type Role } from '@bda/shared';
import type { RuntimeConfig } from '../config';

export const RETURN_TO_KEY = 'bda:returnTo';
export const CALLBACK_PATH = '/auth/callback';

export function configureAuth(config: RuntimeConfig, origin = window.location.origin): void {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config.userPoolId,
        userPoolClientId: config.userPoolClientId,
        loginWith: {
          oauth: {
            domain: config.cognitoDomain,
            scopes: ['openid', 'email', 'profile'],
            redirectSignIn: [`${origin}${CALLBACK_PATH}`],
            redirectSignOut: [`${origin}/`],
            responseType: 'code',
          },
        },
      },
    },
  });
  // Tokens live in sessionStorage, never localStorage (requirement 1.3).
  cognitoUserPoolsTokenProvider.setKeyValueStorage(amplifySessionStorage);
}

export interface SessionUser {
  sub: string;
  name: string;
  email: string;
  roles: Role[];
}

export function rolesFromClaim(claim: unknown): Role[] {
  const list = Array.isArray(claim) ? claim.map(String) : typeof claim === 'string' ? claim.split(/[\s,]+/) : [];
  return list.filter((r): r is Role => ROLES.includes(r as Role));
}

export async function currentUser(forceRefresh = false): Promise<SessionUser | null> {
  try {
    const session = await fetchAuthSession({ forceRefresh });
    const id = session.tokens?.idToken?.payload;
    if (!id || !session.tokens?.accessToken) return null;
    return {
      sub: String(id.sub),
      name: String(id.name ?? id.email ?? ''),
      email: String(id.email ?? ''),
      roles: rolesFromClaim(id['cognito:groups']),
    };
  } catch {
    return null;
  }
}

export async function accessToken(forceRefresh = false): Promise<string | null> {
  try {
    const session = await fetchAuthSession({ forceRefresh });
    return session.tokens?.accessToken?.toString() ?? null;
  } catch {
    return null;
  }
}

export async function signIn(returnTo: string): Promise<void> {
  window.sessionStorage.setItem(RETURN_TO_KEY, returnTo);
  await signInWithRedirect();
}

export function consumeReturnTo(): string {
  const v = window.sessionStorage.getItem(RETURN_TO_KEY);
  window.sessionStorage.removeItem(RETURN_TO_KEY);
  return v && v.startsWith('/') && !v.startsWith('//') ? v : '/';
}

/**
 * Sign-out (requirement 1.5): revokes this session's refresh token with RevokeToken (the app client has token
 * revocation on), clears the stored tokens, and ends the Hosted UI session through its logout endpoint.
 *
 * Not `global: true`: GlobalSignOut needs the `aws.cognito.signin.user.admin` scope, which Hosted UI tokens for
 * this client do not carry, and Amplify ignores the resulting error, so the refresh token stayed valid (found by
 * the task 12.3 end-to-end check).
 */
export async function signOut(): Promise<void> {
  await amplifySignOut({ global: false });
}

/**
 * Fired synchronously just before the app leaves for sign-in because the session could not be refreshed.
 * The editor listens for it to write its draft to sessionStorage at once (requirement 1.4, design 7).
 */
export const SESSION_EXPIRED_EVENT = 'bda:session-expired';

let expiring = false;

/**
 * Called by the API client when a request is still 401 after a forced token refresh (design 7). Lets listeners
 * save state, then starts sign-in with the current route as the return target, so the user comes back to the
 * page they were on (for the editor, with the draft restored). Concurrent 401s start one sign-in.
 */
export async function handleSessionExpired(
  start: (returnTo: string) => Promise<void> = signIn,
  loc: Pick<Location, 'pathname' | 'search' | 'hash'> = window.location,
): Promise<void> {
  if (expiring) return;
  expiring = true;
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  try {
    await start(`${loc.pathname}${loc.search}${loc.hash}`);
  } catch {
    // Amplify still holds tokens the API rejected; a full reload lets the route guard decide.
    expiring = false;
    window.location.assign(`${loc.pathname}${loc.search}`);
  }
  // On success the page is leaving for the Hosted UI, so later 401s from requests in flight are ignored.
}
